import { 
  Container, 
  Flex, 
  Text, 
  VStack, 
  Box, 
  Grid, 
  GridItem,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Progress,
  Badge,
  Skeleton,
  Divider
} from "@chakra-ui/react";
import { useEffect } from "react";
import { useWallet } from "@solana/wallet-adapter-react";
import { useErrorToast } from "../helpers/toast";
import { useAirdropData } from "../hooks/useAirdropData";
import { solAmountFormatter } from "../helpers/formatter";

export const AirdropPage = () => {
  const { publicKey } = useWallet();
  const { airdropData, isLoading, error, fetchAirdropData } = useAirdropData();
  const { errorToast } = useErrorToast();

  useEffect(() => {
    if (error) {
      errorToast(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  useEffect(() => {
    fetchAirdropData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicKey]);

  useEffect(() => {
    console.log(airdropData);
  }, [airdropData]);

  // Get the airdrop data
  const airdrop = airdropData;

  // Calculate metrics
  const totalAmount = airdrop ? Math.round(Number(airdrop.maxTotalClaim) / 1e9) : 0;
  const claimedAmount = airdrop ? Math.round(Number(airdrop.totalAmountClaimed) / 1e9) : 0;
  const unclaimedAmount = totalAmount - claimedAmount;
  const claimedRatio = totalAmount > 0 ? (claimedAmount / totalAmount) * 100 : 0;
  
  const totalWallets = airdrop ? airdrop.maxNumNodes : 0;
  const claimedWallets = airdrop ? airdrop.numNodesClaimed : 0;
  const walletsRatio = totalWallets > 0 ? (claimedWallets / totalWallets) * 100 : 0;

  // Format dates
  const formatDate = (timestamp: number) => {
    if (!timestamp) return "N/A";
    return new Date(timestamp * 1000).toLocaleString();
  };

  return (
    <>
      <Flex direction="column" minHeight="100vh">
        <Container maxW="1200px" flex="1" py={8}>
          <VStack spacing={8} align="stretch">
            <Flex alignItems="center" justifyContent="flex-start">
              <Text
                fontWeight="bold"
                fontSize="2xl"
                color="white"
                lineHeight="1.2"
                mr={2}
              >
                Airdrop Dashboard
              </Text>
            </Flex>

            {/* Token Amount Statistics */}
            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={6}>
              <GridItem>
                <Box
                  bg="rgba(255, 255, 255, 0.05)"
                  borderRadius="lg"
                  p={6}
                  border="1px solid"
                  borderColor="rgba(255, 255, 255, 0.1)"
                >
                  <Skeleton isLoaded={!isLoading}>
                    <Stat>
                      <StatLabel color="gray.400" fontSize="sm">
                        Amount Claimed / Total
                      </StatLabel>
                      <StatNumber color="white" fontSize="2xl" mt={2}>
                        {solAmountFormatter.format(claimedAmount)} / {solAmountFormatter.format(totalAmount)}
                      </StatNumber>
                      <StatHelpText color="gray.400" mt={2}>
                        {claimedRatio.toFixed(2)}% claimed
                      </StatHelpText>
                      <Progress 
                        value={claimedRatio} 
                        colorScheme="green" 
                        size="sm" 
                        mt={3}
                        borderRadius="full"
                      />
                    </Stat>
                  </Skeleton>
                </Box>
              </GridItem>

              <GridItem>
                <Box
                  bg="rgba(255, 255, 255, 0.05)"
                  borderRadius="lg"
                  p={6}
                  border="1px solid"
                  borderColor="rgba(255, 255, 255, 0.1)"
                >
                  <Skeleton isLoaded={!isLoading}>
                    <Stat>
                      <StatLabel color="gray.400" fontSize="sm">
                        Amount Unclaimed
                      </StatLabel>
                      <StatNumber color="white" fontSize="2xl" mt={2}>
                        {solAmountFormatter.format(unclaimedAmount)}
                      </StatNumber>
                      <StatHelpText color="gray.400" mt={2}>
                        {(100 - claimedRatio).toFixed(2)}% remaining
                      </StatHelpText>
                    </Stat>
                  </Skeleton>
                </Box>
              </GridItem>

              <GridItem>
                <Box
                  bg="rgba(255, 255, 255, 0.05)"
                  borderRadius="lg"
                  p={6}
                  border="1px solid"
                  borderColor="rgba(255, 255, 255, 0.1)"
                >
                  <Skeleton isLoaded={!isLoading}>
                    <Stat>
                      <StatLabel color="gray.400" fontSize="sm">
                        Wallets Claimed / Total
                      </StatLabel>
                      <StatNumber color="white" fontSize="2xl" mt={2}>
                        {claimedWallets} / {totalWallets}
                      </StatNumber>
                      <StatHelpText color="gray.400" mt={2}>
                        {walletsRatio.toFixed(2)}% of wallets
                      </StatHelpText>
                      <Progress 
                        value={walletsRatio} 
                        colorScheme="blue" 
                        size="sm" 
                        mt={3}
                        borderRadius="full"
                      />
                    </Stat>
                  </Skeleton>
                </Box>
              </GridItem>

              {/* Dates Section */}
              <GridItem>
                <Box
                  bg="rgba(255, 255, 255, 0.05)"
                  borderRadius="lg"
                  p={6}
                  border="1px solid"
                  borderColor="rgba(255, 255, 255, 0.1)"
                >
                  <Skeleton isLoaded={!isLoading}>
                    <Text color="gray.400" fontSize="sm" fontWeight="bold" mb={4}>
                      Important Dates
                    </Text>
                    <VStack align="stretch" spacing={3}>
                      <Flex justify="space-between">
                        <Text color="gray.400" fontSize="sm">Start Date:</Text>
                        <Text color="white" fontSize="sm">
                          {airdrop ? formatDate(airdrop.startTs) : "N/A"}
                        </Text>
                      </Flex>
                      <Divider borderColor="rgba(255, 255, 255, 0.1)" />
                      <Flex justify="space-between">
                        <Text color="gray.400" fontSize="sm">End Date:</Text>
                        <Text color="white" fontSize="sm">
                          {airdrop ? formatDate(airdrop.endTs) : "N/A"}
                        </Text>
                      </Flex>
                      <Divider borderColor="rgba(255, 255, 255, 0.1)" />
                      <Flex justify="space-between">
                        <Text color="gray.400" fontSize="sm">Clawback Start:</Text>
                        <Text color="white" fontSize="sm">
                          {airdrop ? formatDate(airdrop.clawbackStartTs) : "N/A"}
                        </Text>
                      </Flex>
                    </VStack>
                  </Skeleton>
                </Box>
              </GridItem>
            </Grid>

            {/* Admin Information */}
            <Box
              bg="rgba(255, 255, 255, 0.05)"
              borderRadius="lg"
              p={6}
              border="1px solid"
              borderColor="rgba(255, 255, 255, 0.1)"
            >
              <Skeleton isLoaded={!isLoading}>
                <Text color="gray.400" fontSize="sm" fontWeight="bold" mb={4}>
                  Admin Information
                </Text>
                <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
                  <Flex align="center">
                    <Text color="gray.400" fontSize="sm" mr={2}>Admin:</Text>
                    <Text 
                      color="white" 
                      fontSize="sm" 
                      fontFamily="mono"
                      isTruncated
                      maxW="300px"
                    >
                      {airdrop?.admin || "N/A"}
                    </Text>
                  </Flex>
                  <Flex align="center" gap={4}>
                    <Flex align="center">
                      <Text color="gray.400" fontSize="sm" mr={2}>Clawed Back:</Text>
                      <Badge colorScheme={airdrop?.clawedBack ? "red" : "green"}>
                        {airdrop?.clawedBack ? "Yes" : "No"}
                      </Badge>
                    </Flex>
                    <Flex align="center">
                      <Text color="gray.400" fontSize="sm" mr={2}>Closable:</Text>
                      <Badge colorScheme={airdrop?.closable ? "green" : "gray"}>
                        {airdrop?.closable ? "Yes" : "No"}
                      </Badge>
                    </Flex>
                  </Flex>
                </Grid>
              </Skeleton>
            </Box>
          </VStack>
        </Container>
      </Flex>
    </>
  );
};
